import React, { useState } from 'react';
import PrivacyPolicy from './PrivacyPolicy';

function WaitlistModal({ onClose, context }) {
  const isPro = context && context !== 'beta';
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPrivacy, setShowPrivacy] = useState(false)

  const handleSubmit = async () => {
    if (!email || !email.includes('@')) {
      setError('Please enter a valid email address');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await fetch('/api/waitlist', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, source: context || 'landing_pro' }),
      });

      if (response.ok) {
        setSubmitted(true);
      } else {
        setError('Something went wrong. Please try again.');
      }
    } catch (err) {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSubmit();
    }
  };

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <button style={styles.closeButton} onClick={onClose}>×</button>

        {!submitted ? (
          <>
            <div style={styles.badge}>{isPro ? 'Pro — coming soon' : 'Coming Soon'}</div>
            <h2 style={styles.title}>
              {isPro ? 'Join the ApptNote Pro waitlist' : 'Join the ApptNote waitlist'}
            </h2>
            <p style={styles.description}>
              {isPro
                ? "Be first to know when Pro launches. You'll get early access and an exclusive launch price."
                : "For women in their 30s and 40s wondering if it's perimenopause. Be among the first 50 to get early access — free during beta."}
            </p>

            <div style={styles.inputWrapper}>
              <input
                type="email"
                placeholder="Your email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyPress={handleKeyPress}
                style={styles.input}
                disabled={loading}
              />
              {error && <p style={styles.error}>{error}</p>}
              <p style={styles.consent}>
                By joining the waitlist, you agree to our{' '}
                <button 
                  style={styles.consentLink}
                  onClick={() => setShowPrivacy(true)}
                >
                  Privacy Policy
                </button>. 
                We'll only email you about ApptNote.
              </p>
              <button
                style={loading ? styles.buttonLoading : styles.button}
                onClick={handleSubmit}
                disabled={loading}
              >
                {loading ? 'Joining...' : 'Join the Waitlist'}
              </button>
            </div>

            <p style={styles.disclaimer}>
              No spam. We'll only email you when early access is ready.
            </p>
          </>
        ) : (
          <>
            <div style={styles.successIcon}>🌿</div>
            <h2 style={styles.title}>You're on the list!</h2>
            <p style={styles.description}>
              {isPro
                ? "We'll email you as soon as Pro launches with your exclusive early access price. 🌿"
                : "Thank you for joining the waitlist. We'll email you as soon as early access opens — keep an eye on your inbox 🌿"}
            </p>
            <button style={styles.button} onClick={onClose}>
              Close
            </button>
          </>
        )}
      </div>
      {showPrivacy && <PrivacyPolicy onClose={() => setShowPrivacy(false)} />}
    </div>
  );
}

const styles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(44, 44, 42, 0.6)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: '20px',
    fontFamily: "'Segoe UI', system-ui, sans-serif",
  },
  modal: {
    backgroundColor: '#FAF7F2',
    borderRadius: '24px',
    padding: '48px 40px',
    maxWidth: '480px',
    width: '100%',
    position: 'relative',
    textAlign: 'center',
    boxShadow: '0 20px 60px rgba(44,44,42,0.2)',
  },
  closeButton: {
    position: 'absolute',
    top: '16px',
    right: '20px',
    background: 'none',
    border: 'none',
    fontSize: '28px',
    cursor: 'pointer',
    color: '#6c8396',
    lineHeight: '1',
    padding: '4px 8px',
  },
  badge: {
    display: 'inline-block',
    backgroundColor: '#D4A5A5',
    color: 'white',
    padding: '6px 16px',
    borderRadius: '100px',
    fontSize: '12px',
    fontWeight: '600',
    marginBottom: '20px',
    letterSpacing: '0.05em',
  },
  title: {
    fontSize: '28px',
    fontWeight: '700',
    color: '#2C2C2A',
    marginBottom: '16px',
    lineHeight: '1.2',
  },
  description: {
    fontSize: '16px',
    color: '#4a6070',
    lineHeight: '1.6',
    marginBottom: '32px',
  },
  inputWrapper: {
    marginBottom: '16px',
  },
  input: {
    width: '100%',
    padding: '16px 20px',
    fontSize: '16px',
    border: '1.5px solid #e1e8ed',
    borderRadius: '12px',
    marginBottom: '12px',
    boxSizing: 'border-box',
    fontFamily: 'inherit',
    backgroundColor: 'white',
    color: '#2C2C2A',
  },
  button: {
    width: '100%',
    padding: '16px',
    backgroundColor: '#D4A5A5',
    color: 'white',
    border: 'none',
    borderRadius: '12px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    fontFamily: 'inherit',
  },
  buttonLoading: {
    width: '100%',
    padding: '16px',
    backgroundColor: '#c9a0a0',
    color: 'white',
    border: 'none',
    borderRadius: '12px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'not-allowed',
    fontFamily: 'inherit',
  },
  error: {
    color: '#c44',
    fontSize: '14px',
    marginTop: '-4px',
    marginBottom: '12px',
    textAlign: 'left',
  },
  disclaimer: {
    fontSize: '13px',
    color: '#6c8396',
    marginTop: '20px',
    margin: '20px 0 0',
  },
  successIcon: {
    fontSize: '48px',
    marginBottom: '16px',
  },
  consent: {
  fontSize: '13px',
  color: '#4a6070',
  lineHeight: '1.6',
  textAlign: 'center',
  margin: '12px 0 16px',
},
  consentLink: {
    background: 'none',
    border: 'none',
    color: '#D4A5A5',
    textDecoration: 'underline',
    cursor: 'pointer',
    padding: '0',
    fontFamily: 'inherit',
    fontSize: 'inherit',
    fontWeight: '500',
},
};

export default WaitlistModal;